import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserCard } from '../hooks/useUserCard';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { ArrowLeft, Edit, Trash2, Mail, Phone, MapPin, Globe, Briefcase, User, Building2, Linkedin, Twitter, Github, Instagram, Youtube, Facebook, MessageCircle, Download, FileText } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { userCard, loading: cardLoading } = useUserCard();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    email: '',
    phone: '',
    phone2: '',
    websiteName: '',
    websiteTLD: '',
    location: '',
    locationAddress: '',
    bio: '',
    linkedin: '',
    twitter: '',
    github: '',
    instagram: '',
    youtube: '',
    facebook: '',
    whatsapp: ''
  });

  useEffect(() => {
    if (cardLoading) return;
    
    if (!user) {
      navigate('/');
      return;
    }
    
    loadContact();
  }, [id, user, cardLoading]);

  const loadContact = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { scannedContactAPI } = await import('../services/api');
      const fetchedContact = await scannedContactAPI.getById(id);
      setContact(fetchedContact);
      
      // Populate form data
      setFormData({
        name: fetchedContact.name || '',
        title: fetchedContact.title || '',
        company: fetchedContact.company || '',
        email: fetchedContact.email || '',
        phone: fetchedContact.phone || '',
        phone2: fetchedContact.phone2 || '',
        websiteName: fetchedContact.websiteName || '',
        websiteTLD: fetchedContact.websiteTLD || '',
        location: fetchedContact.location || '',
        locationAddress: fetchedContact.locationAddress || '',
        bio: fetchedContact.bio || '',
        linkedin: fetchedContact.linkedin || '',
        twitter: fetchedContact.twitter || '',
        github: fetchedContact.github || '',
        instagram: fetchedContact.instagram || '',
        youtube: fetchedContact.youtube || '',
        facebook: fetchedContact.facebook || '',
        whatsapp: fetchedContact.whatsapp || ''
      });
    } catch (error) {
      console.error('Error loading contact:', error);
      toast({
        title: "Error",
        description: "Failed to load contact details. Please try again.",
        variant: "destructive"
      });
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSave = async () => {
    try {
      const { scannedContactAPI } = await import('../services/api');
      await scannedContactAPI.update(id, formData);
      
      await loadContact();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast({
        title: "Error",
        description: "Failed to update contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async () => {
    try {
      const { scannedContactAPI } = await import('../services/api');
      await scannedContactAPI.delete(id);
      
      navigate('/contacts');
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: "Error",
        description: "Failed to delete contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Sync notes when dialog opens - MUST be before any early returns
  useEffect(() => {
    if (notesDialogOpen && contact) {
      setNotesText(contact.notes || '');
    }
  }, [notesDialogOpen, contact]);

  if (loading || !contact) {
    return (
      <Layout userCard={userCard} loading={cardLoading}>
        <div className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-gray-100 p-12 text-center">
              <div className="inline-block mb-4">
                <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 text-lg">Loading contact...</p>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  const handleDownloadVCard = async () => {
    try {
      // Processing notification removed for cleaner UX

      // Import vCard generator
      const { generateVCard } = await import('../utils/vcardGenerator');
      
      // Build card object from contact data
      const cardData = {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        phone2: contact.phone2,
        company: contact.company,
        title: contact.title,
        website: contact.websiteName ? `https://${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD || ''}` : '',
        location: contact.location,
        locationAddress: contact.locationAddress,
        bio: contact.bio,
        notes: contact.notes,
        profileImage: contact.profileImage,
        linkedin: contact.linkedin,
        twitter: contact.twitter,
        github: contact.github,
        instagram: contact.instagram,
        facebook: contact.facebook,
        youtube: contact.youtube,
        whatsapp: contact.whatsapp
      };
      
      // Generate vCard
      const vCardData = await generateVCard(cardData, false); // without photo for smaller size
      
      // Download vCard
      const { downloadVCard } = await import('../utils/vcardGenerator');
      await downloadVCard(vCardData, contact.name || 'contact');
      
      // Download complete notification removed for cleaner UX
    } catch (error) {
      console.error('Error downloading vCard:', error);
      toast({
        title: "Error",
        description: "Failed to download contact. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSaveNotes = async () => {
    try {
      const { scannedContactAPI } = await import('../services/api');
      // Use _id (from backend) or id (if available)
      const contactId = contact._id || contact.id;
      await scannedContactAPI.update(contactId, { notes: notesText });
      
      setContact({ ...contact, notes: notesText });
      setNotesDialogOpen(false);
      
      // Notification removed for cleaner UX
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error",
        description: "Failed to save notes. Please try again.",
        variant: "destructive"
      });
    }
  };

  const InfoField = ({ icon: Icon, label, value, link }) => {
    if (!value) return null;
    
    const handleClick = () => {
      if (link) {
        window.open(link, '_blank', 'noopener,noreferrer');
      }
    };
    
    return (
      <div 
        className={`flex items-start gap-3 p-4 bg-gray-50 rounded-lg ${link ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
        onClick={handleClick}
      >
        <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${link ? 'text-orange-600 hover:text-orange-700' : 'text-gray-600'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-600 mb-1">{label}</p>
          {link ? (
            <p className="text-gray-900 hover:text-orange-600 transition-colors break-words">
              {value}
            </p>
          ) : (
            <p className="text-gray-900 break-words">{value}</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout userCard={userCard} loading={cardLoading}>
      <SEO 
        title={`${contact.name} - Contact Details`}
        description={`View contact details for ${contact.name}`}
        noindex={true}
      />
      
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/contacts')}
            className="mb-6 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Contacts
          </Button>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Contact <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">Details</span>
            </h1>
          </div>

          {/* Card Images */}
          {(contact.frontImage || contact.backImage || contact.frontImageUrl) && (
            <Card className="p-6 mb-6 border-2 border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Scanned Card Images</h2>
              <div className="flex gap-4 flex-wrap">
                {(contact.frontImage || contact.frontImageUrl) && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Front</p>
                    <img 
                      src={contact.frontImage || contact.frontImageUrl} 
                      alt="Front of card" 
                      className="max-w-sm w-full h-auto object-contain rounded-lg border-2 border-gray-300"
                    />
                  </div>
                )}
                {contact.backImage && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Back</p>
                    <img 
                      src={contact.backImage} 
                      alt="Back of card" 
                      className="max-w-sm w-full h-auto object-contain rounded-lg border-2 border-gray-300"
                    />
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Contact Information */}
          <Card className="p-6 mb-6 border-2 border-gray-200 relative">
            {/* Action Icons - Top Right Corner */}
            <div className="absolute top-4 right-4 flex gap-2">
              {/* Download Contact Button */}
              <button
                onClick={handleDownloadVCard}
                className="w-10 h-10 bg-white hover:bg-teal-500 text-teal-600 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all border-2 border-teal-200 hover:border-teal-500"
                title="Download Contact"
              >
                <Download className="w-5 h-5" />
              </button>
              
              {/* Notes Button */}
              <div className="relative">
                <button
                  onClick={() => setNotesDialogOpen(true)}
                  className="w-10 h-10 bg-white hover:bg-amber-500 text-amber-600 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all border-2 border-amber-200 hover:border-amber-500"
                  title="View/Edit Notes"
                >
                  <FileText className="w-5 h-5" />
                </button>
                {/* Notes count badge */}
                {contact.notes && contact.notes.trim().length > 0 && (() => {
                  const notesLineCount = contact.notes.split('\n').filter(line => line.trim()).length;
                  return notesLineCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] h-5 flex items-center justify-center shadow-md border-2 border-white">
                      {notesLineCount}
                    </span>
                  );
                })()}
              </div>
              
              {/* Edit Button */}
              <button
                onClick={() => setEditDialogOpen(true)}
                className="w-10 h-10 bg-white hover:bg-orange-500 text-orange-600 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all border-2 border-orange-200 hover:border-orange-500"
                title="Edit Contact"
              >
                <Edit className="w-5 h-5" />
              </button>
              
              {/* Delete Button */}
              <button
                onClick={() => setDeleteDialogOpen(true)}
                className="w-10 h-10 bg-white hover:bg-red-500 text-red-600 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all border-2 border-red-200 hover:border-red-500"
                title="Delete Contact"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">{contact.name}</h2>
              {contact.title && <p className="text-sm text-gray-600 mb-1">{contact.title}</p>}
              {contact.company && <p className="text-sm text-gray-700 font-medium flex items-center gap-2">
                <Building2 className="w-5 h-5 text-orange-600" />
                {contact.company}
              </p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoField icon={Mail} label="Email" value={contact.email} link={contact.email && `mailto:${contact.email}`} />
              <InfoField icon={Phone} label="Phone" value={contact.phone} link={contact.phone && `tel:${contact.phone}`} />
              <InfoField icon={Phone} label="Office Number" value={contact.phone2} link={contact.phone2 && `tel:${contact.phone2}`} />
              <InfoField 
                icon={Globe} 
                label="Website" 
                value={contact.websiteName ? `${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD || ''}` : ''} 
                link={contact.websiteName && `https://${contact.websiteName}${contact.websiteTLD?.startsWith('.') ? '' : '.'}${contact.websiteTLD || ''}`} 
              />
              <InfoField 
                icon={MapPin} 
                label="Location" 
                value={contact.location} 
                link={contact.location && `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.location)}`}
              />
            </div>

            {contact.locationAddress && (
              <div className="mt-4">
                <InfoField 
                  icon={MapPin} 
                  label="Address" 
                  value={contact.locationAddress} 
                  link={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contact.locationAddress)}`}
                />
              </div>
            )}

            {contact.bio && (
              <div className="mt-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-semibold text-gray-600 mb-2">Bio</p>
                  <p className="text-gray-900 whitespace-pre-wrap">{contact.bio}</p>
                </div>
              </div>
            )}
          </Card>

          {/* Social Links */}
          {(contact.linkedin || contact.twitter || contact.github || contact.instagram || contact.youtube || contact.facebook || contact.whatsapp) && (
            <Card className="p-6 border-2 border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Social Links</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField icon={Linkedin} label="LinkedIn" value={contact.linkedin} link={contact.linkedin && `https://linkedin.com/in/${contact.linkedin}`} />
                <InfoField icon={Twitter} label="Twitter" value={contact.twitter} link={contact.twitter && `https://twitter.com/${contact.twitter}`} />
                <InfoField icon={Github} label="GitHub" value={contact.github} link={contact.github && `https://github.com/${contact.github}`} />
                <InfoField icon={Instagram} label="Instagram" value={contact.instagram} link={contact.instagram && `https://instagram.com/${contact.instagram}`} />
                <InfoField icon={Youtube} label="YouTube" value={contact.youtube} link={contact.youtube && `https://youtube.com/@${contact.youtube}`} />
                <InfoField icon={Facebook} label="Facebook" value={contact.facebook} link={contact.facebook && `https://facebook.com/${contact.facebook}`} />
                <InfoField icon={MessageCircle} label="WhatsApp" value={contact.whatsapp} link={contact.whatsapp && `https://wa.me/${contact.whatsapp}`} />
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
              Edit Contact
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Full Name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Job Title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                <Input
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  placeholder="Company Name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Office Number</label>
                <Input
                  value={formData.phone2}
                  onChange={(e) => handleInputChange('phone2', e.target.value)}
                  placeholder="+1234567890"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Website Name</label>
                <Input
                  value={formData.websiteName}
                  onChange={(e) => handleInputChange('websiteName', e.target.value)}
                  placeholder="example"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Website TLD</label>
                <Input
                  value={formData.websiteTLD}
                  onChange={(e) => handleInputChange('websiteTLD', e.target.value)}
                  placeholder=".com"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
              <Input
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="City, Country"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Address</label>
              <Input
                value={formData.locationAddress}
                onChange={(e) => handleInputChange('locationAddress', e.target.value)}
                placeholder="Full Address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Brief description"
                className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            
            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-700 mb-3">Social Links</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">LinkedIn</label>
                  <Input
                    value={formData.linkedin}
                    onChange={(e) => handleInputChange('linkedin', e.target.value)}
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Twitter</label>
                  <Input
                    value={formData.twitter}
                    onChange={(e) => handleInputChange('twitter', e.target.value)}
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">GitHub</label>
                  <Input
                    value={formData.github}
                    onChange={(e) => handleInputChange('github', e.target.value)}
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Instagram</label>
                  <Input
                    value={formData.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">YouTube</label>
                  <Input
                    value={formData.youtube}
                    onChange={(e) => handleInputChange('youtube', e.target.value)}
                    placeholder="@channel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Facebook</label>
                  <Input
                    value={formData.facebook}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                    placeholder="username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">WhatsApp</label>
                  <Input
                    value={formData.whatsapp}
                    onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                    placeholder="phone number"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditSave}
              className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Contact"
        message={`Are you sure you want to delete "${contact?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Notes Dialog */}
      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Contact Notes
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notes for {contact?.name}
              </label>
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Add notes about this contact..."
                className="w-full min-h-[300px] p-4 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none resize-vertical"
              />
              <p className="text-xs text-gray-500 mt-2">
                Use notes to remember important details about this contact
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setNotesDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveNotes}
              className="bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-700 hover:to-yellow-600 text-white"
            >
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ContactDetail;
